import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { Dialog, TextField } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import RateReviewRoundedIcon from "@mui/icons-material/RateReviewRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { selectRealUser } from "../../features/userSlice";
import { getImpersonation } from "../../utils/impersonation";
import {
  dismissCheckIn,
  fetchCheckInStatus,
  sendFeedback,
} from "../../views/Admin/Feedback/feedbackApi";
import { holdBackFeatureRequestPrompt } from "./FeatureRequestBubble";

// Monthly feedback check-in: a popup asking whether there's anything people
// would like added, changed, or fixed. Sending feedback or dismissing it
// starts the server's 30-day timer (server/routes/api/Feedback/
// feedbackCheckinHandler.js), so it follows the person across browsers and
// admins can see where everyone stands on the Monthly Feedback page.
// Rendered from NavListAuth, so it only shows on pages with the header.

const OPEN_DELAY_MS = 2500;

// Pages where the popup would be redundant or in the way. It waits there
// and opens on the next page instead.
const HIDDEN_PATHS = [
  "/forms/feature-request",
  "/admin/feedback",
  "/inituser",
  "/logout",
];

// NavListAuth remounts on every route change, so whether it's due is
// fetched once per page load and kept here, along with an unsent draft.
let dueFor = null; // user id the cached answer belongs to
let duePromise = null; // resolves to { due, intervalDays }
let openedThisLoad = false;
let draft = "";

const loadStatus = (userId) => {
  if (dueFor !== userId) {
    dueFor = userId;
    duePromise = fetchCheckInStatus(userId)
      .then((status) => ({ due: Boolean(status.due), intervalDays: status.intervalDays || 30 }))
      // Never nag on a failed check; it tries again on the next page load.
      .catch(() => ({ due: false, intervalDays: 30 }));
  }
  return duePromise;
};

const markAnswered = (intervalDays) => {
  duePromise = Promise.resolve({ due: false, intervalDays });
  draft = "";
};

const FeedbackCheckIn = () => {
  // Always the signed-in person, never an admin's preview of someone else.
  const user = useSelector(selectRealUser);
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState(draft);
  const [intervalDays, setIntervalDays] = useState(30);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(false);

  const hidden = HIDDEN_PATHS.some((path) =>
    location.pathname.toLowerCase().startsWith(path)
  );

  useEffect(() => {
    if (!user?.id || getImpersonation() || hidden) return undefined;
    let live = true;
    let timer = null;
    loadStatus(user.id).then((status) => {
      if (!live || !status.due) return;
      setIntervalDays(status.intervalDays);
      // One popup at a time: the feature-request card skips this session.
      holdBackFeatureRequestPrompt();
      // A few seconds in the first time, so it doesn't compete with the
      // page they came for; straight back if they navigated with it open.
      timer = setTimeout(
        () => {
          if (!live) return;
          openedThisLoad = true;
          setOpen(true);
        },
        openedThisLoad ? 0 : OPEN_DELAY_MS
      );
    });
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [user?.id, hidden]);

  if (!open) return null;

  const firstName = String(user?.name || "").split(" ")[0];

  const updateMessage = (value) => {
    draft = value;
    setMessage(value);
  };

  const dismiss = () => {
    if (busy) return;
    setOpen(false);
    markAnswered(intervalDays);
    // If this fails the timer doesn't start and it asks again next visit.
    dismissCheckIn(user.id).catch(() => {});
  };

  const close = () => setOpen(false);

  const submit = async () => {
    if (busy) return;
    if (!message.trim()) return setError("Write your feedback first.");
    setBusy(true);
    setError(null);
    try {
      await sendFeedback(user.id, message.trim(), location.pathname);
      markAnswered(intervalDays);
      setSent(true);
    } catch (e) {
      setError(e.message || "Couldn't send your feedback. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      onClose={(event, reason) => {
        // A stray click outside shouldn't throw away what they typed.
        if (reason === "backdropClick") return;
        if (sent) close();
        else if (!message.trim()) dismiss();
      }}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { m: 2 } }}
      aria-labelledby="feedback-checkin-title"
    >
      {sent ? (
        <div className="p-5 text-center sm:p-6">
          <span className="app-icon-tile h-12 w-12 rounded-2xl bg-emerald-500/15 text-emerald-600 dark:text-emerald-300">
            <CheckCircleRoundedIcon />
          </span>
          <h2 id="feedback-checkin-title" className="mt-3 text-lg font-semibold">
            Thanks{firstName ? `, ${firstName}` : ""}!
          </h2>
          <p className="app-muted mx-auto mt-1 max-w-sm text-sm">
            Your feedback went to the team that builds Node, and every
            response gets read. We'll check in again in {intervalDays} days.
          </p>
          <button
            type="button"
            className="app-btn app-btn-primary mt-5"
            onClick={close}
          >
            Close
          </button>
        </div>
      ) : (
        <div className="p-5 sm:p-6">
          <div className="flex items-start gap-3">
            <span className="app-icon-tile h-10 w-10 shrink-0 rounded-xl bg-amber-500/15 text-amber-600 dark:text-amber-300">
              <RateReviewRoundedIcon fontSize="small" />
            </span>
            <div className="min-w-0 flex-1">
              <h2 id="feedback-checkin-title" className="text-lg font-semibold">
                Any feedback for Node?
              </h2>
              <p className="app-muted mt-1 text-sm">
                Once a month we check in: is there anything you'd like added,
                changed, or fixed? Big or small, it all helps.
              </p>
            </div>
            <button
              type="button"
              onClick={dismiss}
              aria-label="Dismiss feedback check-in"
              className="app-icon-btn -mr-1 -mt-1 shrink-0"
            >
              <CloseRoundedIcon fontSize="small" />
            </button>
          </div>

          <TextField
            className="!mt-5"
            fullWidth
            multiline
            minRows={4}
            autoFocus
            label="Your feedback"
            placeholder="e.g. It would save me time if… / I wish I could… / This page is confusing because…"
            value={message}
            onChange={(e) => updateMessage(e.target.value)}
            inputProps={{ maxLength: 4000 }}
          />

          {error && (
            <p
              role="alert"
              className="mt-3 rounded-xl bg-rose-500/10 px-3 py-2 text-sm text-rose-700 dark:text-rose-300"
            >
              {error}
            </p>
          )}

          <div className="mt-5 flex flex-col-reverse gap-3 sm:flex-row sm:items-center">
            <p className="app-muted text-xs sm:mr-auto">
              Sending or choosing Not now hides this for {intervalDays} days.
            </p>
            <div className="flex flex-col-reverse gap-2 sm:flex-row">
              <button
                type="button"
                className="app-btn app-btn-ghost"
                onClick={dismiss}
                disabled={busy}
              >
                Not now
              </button>
              <button
                type="button"
                className="app-btn app-btn-primary"
                onClick={submit}
                disabled={busy || !message.trim()}
              >
                {busy ? "Sending…" : "Send feedback"}
              </button>
            </div>
          </div>
        </div>
      )}
    </Dialog>
  );
};

export default FeedbackCheckIn;
